import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PROJECT } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import { bookingAgeStatus, AGE_STATUS_LABEL, AGE_STATUS_BADGE } from '@/lib/bookingStatus';
import { Plus, Building2, MapPin, Calendar, ArrowRight, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Projects() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_address: '',
    property_type: 'residential',
    project_type: '',
    enquiry_type: ''
  });

  const servicesByCategory = useMemo(() => {
    const grouped = {};
    servicesCatalog.forEach((s) => {
      if (!grouped[s.category]) grouped[s.category] = [];
      grouped[s.category].push(s);
    });
    return grouped;
  }, [servicesCatalog]);

  const selectedService = useMemo(
    () => servicesCatalog.find((s) => s.name === formData.project_type) || null,
    [servicesCatalog, formData.project_type]
  );

  const fetchProjects = useCallback(async () => {
    try {
      const [projectsRes, catalogRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services-catalog`),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(projectsRes.data);
      setBookings(bookingsRes.data);
      const bookable = catalogRes.data.filter((s) => !s.requires_workflow_complete);
      setServicesCatalog(bookable);
      setFormData((prev) =>
        prev.project_type || !bookable.length ? prev : { ...prev, project_type: bookable[0].name }
      );
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Group bookings by age status for the Pre-Assessment Bookings widget
  const bookingsByStatus = useMemo(() => {
    const buckets = { new: [], current: [], old: [] };
    bookings.forEach((b) => {
      const s = bookingAgeStatus(b);
      if (buckets[s]) buckets[s].push(b);
    });
    return buckets;
  }, [bookings]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const enquiry = searchParams.get('enquiry');
    const label = searchParams.get('label');
    const isNew = searchParams.get('new');
    if (enquiry && label) {
      setFormData((prev) => ({
        ...prev,
        title: `${label} Enquiry`,
        description: `New ${label} enquiry. Please describe your specific concerns and requirements below.`,
        enquiry_type: enquiry
      }));
      setShowModal(true);
      setSearchParams({}, { replace: true });
    } else if (isNew) {
      setShowModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      setFormData({
        title: '',
        description: '',
        property_address: '',
        property_type: 'residential',
        project_type: servicesCatalog[0]?.name || '',
        enquiry_type: ''
      });
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-black mb-2" style={{fontFamily: 'Outfit'}}>
              Projects
            </h1>
            <p className="text-slate-600">Manage your construction projects</p>
          </div>
          <button
            data-testid={PROJECT.createButton}
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
          >
            <Plus className="w-5 h-5" />
            New Project
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : (
          <>
            {bookings.length > 0 && (
              <div data-testid="projects-bookings-widget" className="grid-border bg-white p-6 mb-8">
                <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
                  <div>
                    <h2 className="text-xl font-semibold text-black" style={{fontFamily: 'Outfit'}}>
                      Your Pre-Assessment Bookings
                    </h2>
                    <p className="text-sm text-slate-600">
                      Status of each booking — at a glance.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500" /> {bookingsByStatus.new.length} New
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500" /> {bookingsByStatus.current.length} Current
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-slate-400" /> {bookingsByStatus.old.length} Old
                    </span>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {bookings.slice(0, 6).map((b) => {
                    const ageStatus = bookingAgeStatus(b);
                    return (
                      <li
                        key={b.id}
                        data-testid="projects-booking-row"
                        className="flex items-center gap-3 py-3"
                      >
                        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-black truncate">{b.service_type}</div>
                          <div className="text-xs text-slate-500">
                            {new Date(b.created_at).toLocaleDateString()} · A${(b.budget || 0).toFixed(0)} deposit
                            {b.payment_status === 'paid' ? ' · paid' : ' · awaiting payment'}
                          </div>
                        </div>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-sm ${AGE_STATUS_BADGE[ageStatus]}`}
                        >
                          {AGE_STATUS_LABEL[ageStatus]}
                        </span>
                        <button
                          onClick={() => navigate('/payments')}
                          className="text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                        >
                          View
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {bookings.length > 6 && (
                  <div className="mt-3 text-right">
                    <button
                      onClick={() => navigate('/bookings')}
                      className="text-xs text-slate-600 hover:text-black"
                    >
                      View all {bookings.length} bookings →
                    </button>
                  </div>
                )}
              </div>
            )}

            {projects.length === 0 ? (
              <div className="grid-border bg-white p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Outfit'}}>No projects yet</h3>
                <p className="text-slate-600 mb-6">Create your first project to get started</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
                >
                  Create Project
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                data-testid={PROJECT.card}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="grid-border bg-white p-6 hover-lift cursor-pointer"
              >
                <div className="mb-4">
                  <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Outfit'}}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{project.description}</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate">{project.property_address}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(project.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm">
                    {project.property_type}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-sm">
                    {project.status}
                  </span>
                </div>
              </div>
            ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-bold mb-6" style={{fontFamily: 'Outfit'}}>Create New Project</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black mb-2">Project Title</label>
                <input
                  data-testid={PROJECT.titleInput}
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Description</label>
                <textarea
                  data-testid={PROJECT.descriptionInput}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  rows="3"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">Property Address</label>
                <input
                  data-testid={PROJECT.addressInput}
                  type="text"
                  value={formData.property_address}
                  onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">Property Type</label>
                  <select
                    data-testid={PROJECT.propertyTypeSelect}
                    value={formData.property_type}
                    onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                    <option value="multi-unit">Multi-Unit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Service of Interest (Schedule of Reporting Services)
                  </label>
                  <select
                    data-testid={PROJECT.projectTypeSelect}
                    value={formData.project_type}
                    onChange={(e) => setFormData({...formData, project_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:ring-2 focus:ring-blue-600"
                    required
                  >
                    <option value="" disabled>— Choose a service —</option>
                    {Object.keys(servicesByCategory).sort().map((cat) => {
                      const catComingSoon = cat === 'Trade Works & Administration';
                      return (
                        <optgroup key={cat} label={catComingSoon ? `${cat} (Coming Soon)` : cat}>
                          {servicesByCategory[cat].map((s) => {
                            const label = catComingSoon
                              ? `${s.name} — Coming Soon`
                              : `${s.name} — A$${s.deposit_amount.toFixed(0)} deposit`;
                            return (
                              <option key={s.id} value={s.name} disabled={catComingSoon} label={label}>
                                {label}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    })}
                  </select>
                  {selectedService && (
                    <p data-testid="project-service-blurb" className="mt-2 text-xs text-slate-600">
                      Deposit <strong className="text-black">A${selectedService.deposit_amount.toFixed(0)}</strong>
                      {' · '}Full price <strong className="text-black">A${selectedService.full_price.toFixed(0)}</strong>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all"
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
